import { Behavior } from "../Behavior";
import type { IGrid } from "../types";
import type { Particle } from "../Particle";
import { Empty } from "../particles/Empty";

export class MovesLiquid extends Behavior {
    viscosity: number;

    constructor(viscosity: number = 0) {
        super();
        this.viscosity = viscosity;
    }

    update(particle: Particle, grid: IGrid, index: number) {
        // 1. 获取重力并更新速度
        const gx = grid.gravity.x;
        const gy = grid.gravity.y;
        
        // 液体受重力影响较大 (0.5)
        const gravity = 0.5;
        
        particle.velocity.x += gx * gravity;
        particle.velocity.y += gy * gravity;
        
        // 2. 限制速度
        const maxSpeed = 8;
        const speedSq = particle.velocity.x ** 2 + particle.velocity.y ** 2;
        if (speedSq > maxSpeed ** 2) {
            const scale = maxSpeed / Math.sqrt(speedSq);
            particle.velocity.x *= scale;
            particle.velocity.y *= scale;
        }

        // 3. 计算移动步数
        // 增加随机性避免伪影
        const dx = particle.velocity.x + (Math.random() - 0.5) * 0.5;
        const dy = particle.velocity.y + (Math.random() - 0.5) * 0.5;
        
        let steps = Math.floor(Math.max(Math.abs(dx), Math.abs(dy)));
        
        // 液体即使没有速度，如果周围有空位也应该尝试流动 (扩散)
        // 但这里我们主要依赖“受阻后的流动”
        // 如果速度很小，且 steps=0，我们给它一次机会去“流动”
        if (steps <= 0) steps = 1;

        let currentIndex = index;
        let cx = index % grid.width;
        let cy = Math.floor(index / grid.width);

        const stepX = steps > 0 ? dx / steps : 0;
        const stepY = steps > 0 ? dy / steps : 0;

        for (let i = 0; i < steps; i++) {
            // 下一步的理想位置
            const nextX = Math.round(cx + stepX);
            const nextY = Math.round(cy + stepY);
            
            // 检查边界
            if (nextX < 0 || nextX >= grid.width || nextY < 0 || nextY >= grid.height) {
                grid.setIndex(currentIndex, new Empty());
                return -1;
            }
            
            // 坐标未变则跳过 (除非是 steps=1 且速度为0的情况，但 velocity=0 时 stepX/Y 也是0，所以 nextX=cx)
            // 如果 nextX === cx && nextY === cy，说明没有垂直/水平移动，可能是要处理“原地扩散”
            // 但为了简单，如果原地不动，直接跳到流动逻辑
            const isStationary = (nextX === cx && nextY === cy);
            
            let moved = false;
            
            if (!isStationary) {
                const nextIdx = nextY * grid.width + nextX;
                const target = grid.pixels[nextIdx];
                
                // 尝试移动
                if (target.empty) {
                     // 粘度检查：粘度高时，有概率不移动 (模拟流动阻力)
                     if (this.viscosity > 0 && Math.random() < this.viscosity * 0.2) {
                         // 阻滞
                     } else {
                         grid.swap(currentIndex, nextIdx);
                         currentIndex = nextIdx;
                         cx = nextX;
                         cy = nextY;
                         moved = true;
                     }
                } else if (target.name === 'Filter') {
                    // 穿透 Filter
                    // 检查 Filter 沿运动方向的下一格
                    const aheadX = nextX + Math.sign(stepX);
                    const aheadY = nextY + Math.sign(stepY);
                    
                    if (aheadX >= 0 && aheadX < grid.width && aheadY >= 0 && aheadY < grid.height) {
                        const aheadIdx = aheadY * grid.width + aheadX;
                        const aheadParticle = grid.pixels[aheadIdx];
                        if (aheadParticle.empty) {
                            // 跳过 Filter
                            grid.setIndex(aheadIdx, particle);
                            grid.setIndex(currentIndex, new Empty());
                            particle.updated = true;
                            return aheadIdx; // 穿透后直接结束这一帧，简单处理
                        }
                    }
                } else if (target.props.isLiquid) {
                    // 密度处理
                    if (particle.props.density > target.props.density) {
                        if (Math.random() < 0.5) {
                             grid.swap(currentIndex, nextIdx);
                             currentIndex = nextIdx;
                             cx = nextX;
                             cy = nextY;
                             moved = true;
                             // 液体碰撞减速
                             particle.velocity.x *= 0.9;
                             particle.velocity.y *= 0.9;
                        }
                    }
                }
            }

            // 如果受阻 (或原地不动)，尝试流动 (Flow / Slide)
            if (!moved) {
                // 碰撞减速
                particle.velocity.x *= 0.5;
                particle.velocity.y *= 0.5;
                
                // 尝试向垂直于重力的方向流动
                // 流速受粘度影响
                let flowSpeed = 2;
                if (this.viscosity > 0.5) flowSpeed = 1;
                // 在液体表面流动更快
                // 这里简化：统一处理
                
                // 只有当有重力影响时才明确“侧向”，否则随机扩散
                // 实际上 perpendicular logic 依然适用
                
                const flowedIdx = this.flow(grid, currentIndex, cx, cy, gx, gy, flowSpeed);
                if (flowedIdx !== currentIndex) {
                    currentIndex = flowedIdx;
                    cx = currentIndex % grid.width;
                    cy = Math.floor(currentIndex / grid.width);
                    // 流动后
                }
                
                // 液体一旦受阻并尝试流动后，这一帧的动量主要转为横向
                // 我们可以停止垂直尝试
                break;
            }
        }
        
        return currentIndex;
    }

    flow(grid: IGrid, index: number, cx: number, cy: number, gx: number, gy: number, range: number): number {
        // 随机左右方向
        const dir = Math.random() > 0.5 ? 1 : -1;
        
        // 尝试两个方向
        for (const side of [dir, -dir]) {
            // 侧向向量 (垂直于重力)
            const px = -gy * side;
            const py = gx * side;
            
            // 我们沿着侧向寻找空位
            // 这里我们不仅看一步，而是看 range 步
            // 模拟液体的延展性
            
            let currentProbeX = cx;
            let currentProbeY = cy;
            let currentProbeIdx = index;
            
            // 简单的 Raycast 寻找空位? 
            // 不，液体是一格格流过去的。
            // 我们尝试将粒子移动到侧向最远可达的空位
            // 但必须保证路径畅通
            
            for (let r = 1; r <= range; r++) {
                // 目标位置：当前 + 侧向 * r + 微小的重力倾向(模拟下坡)
                // 但主要还是侧向
                const tx = Math.round(cx + px * r);
                const ty = Math.round(cy + py * r);
                
                if (tx < 0 || tx >= grid.width || ty < 0 || ty >= grid.height) break;
                
                const tIdx = ty * grid.width + tx;
                const target = grid.pixels[tIdx];
                
                // 如果是空位，可以移动
                // 或者如果是液体且密度更小
                let canSwap = false;
                if (target.empty) {
                    canSwap = true;
                } else if (target.props.isLiquid && grid.pixels[index].props.density > target.props.density) {
                    canSwap = true;
                } else if (target.name === 'Filter') {
                     // 简单处理：遇到 Filter 停止流动，或者穿透?
                     // 这里简化，停止
                     break;
                } else {
                    // 遇到障碍，停止在这个方向探索
                    break;
                }
                
                if (canSwap) {
                    // 移动到这里
                    grid.swap(currentProbeIdx, tIdx);
                    currentProbeIdx = tIdx;
                    currentProbeX = tx;
                    currentProbeY = ty;
                    
                    // 继续循环，看看能不能流得更远?
                    // 真实物理中，粒子会移动到最远点。
                    // 我们的 swap 已经改变了位置。
                    // 所以下一次循环是从新位置继续?
                    // 不，cx/cy 是原始位置。 tx/ty 是相对于原始位置的偏移。
                    // 所以我们是一步步“跳”过去的吗？
                    // 为了简化，我们只做一次 swap (移动到最远?) 或者每一步都 swap
                    // 这里采用了每一步都 swap 的方式 (因为 update 了 currentProbeIdx)
                    // 但是 loop 是基于 cx, cy 的 (fixed origin)。
                    // 这样会有问题：如果 r=1 移动了，index 变了，r=2 时 tx 还是基于旧 cx 计算的
                    
                    // 修正：我们应该一步步走
                    // 由于 grid.swap 已经移动了粒子，我们必须更新 cx, cy
                    // 但这样 logic 变复杂。
                    // 简化：只流一步。
                    return tIdx;
                }
            }
            
            // 如果这个方向没动，尝试另一个方向
            if (currentProbeIdx !== index) return currentProbeIdx;
        }
        
        return index;
    }
}
